import React, { useState } from "react";
import toast from "react-hot-toast";
import { createProduct, updateProduct } from "../../utils/api";

const INITIAL = {
  customerName: "",
  phoneNumber: "",
  address: "",
  productName: "",
  productPrice: "",
  purchaseDate: "",
  rudrakshaSize: "",
  productDescription: "",
};

function Field({ name, label, required = true, type = "text", form, errors, onChange, ...props }) {
  return (
    <div>
      <label className="label">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        className={`input-field ${errors[name] ? "border-red-500" : ""}`}
        name={name} type={type} value={form[name]} onChange={onChange} {...props}
      />
      {errors[name] && <p className="text-red-500 text-xs mt-1">{errors[name]}</p>}
    </div>
  );
}

export default function ProductForm({ existing, onSuccess, onCancel }) {
  const [form, setForm] = useState(
    existing ? { ...existing, purchaseDate: existing.purchaseDate?.slice(0, 10) } : INITIAL
  );
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.customerName.trim()) e.customerName = "Customer name is required";
    if (!/^[0-9]{10}$/.test(form.phoneNumber)) e.phoneNumber = "Enter valid 10-digit phone number";
    if (!form.address.trim()) e.address = "Address is required";
    if (!form.productName.trim()) e.productName = "Product name is required";
    if (!form.productPrice || form.productPrice <= 0) e.productPrice = "Enter valid price";
    if (!form.purchaseDate) e.purchaseDate = "Purchase date is required";
    else if (new Date(form.purchaseDate) > new Date()) e.purchaseDate = "Purchase date cannot be in the future";
    return e;
  };

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleImages = (e) => {
    const files = [...e.target.files];
    setImages(files);
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
  };

  const removeImage = (idx) => {
    const newImages = images.filter((_, i) => i !== idx);
    const newPreviews = previews.filter((_, i) => i !== idx);
    setImages(newImages);
    setPreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setErrors({});
    setLoading(true);

    try {
      const fd = new FormData();
      // Send form fields (exclude warranty duration — backend sets default)
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("warrantyDuration", "Lifetime");
      fd.append("warrantyDurationMonths", "1200");
      images.forEach((f) => fd.append("images", f));
      videos.forEach((f) => fd.append("videos", f));

      if (existing) {
        await updateProduct(existing._id, fd);
        toast.success("Product updated!");
      } else {
        await createProduct(fd);
        toast.success("Product created successfully!");
      }
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field name="customerName" label="Customer Name" placeholder="Full name" form={form} errors={errors} onChange={handleChange} />
        <div>
          <label className="label">Phone Number <span className="text-red-500">*</span></label>
          <input
            className={`input-field ${errors.phoneNumber ? "border-red-500" : ""}`}
            name="phoneNumber" type="tel" maxLength={10}
            value={form.phoneNumber} onChange={handleChange} placeholder="10-digit number"
          />
          {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
        </div>
        <div className="md:col-span-2">
          <label className="label">Address <span className="text-red-500">*</span></label>
          <textarea
            className={`input-field resize-none ${errors.address ? "border-red-500" : ""}`}
            name="address" rows={2} value={form.address} onChange={handleChange} placeholder="Full address"
          />
          {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
        </div>
        <Field name="productName" label="Product Name" placeholder="e.g. 5 Mukhi Rudraksha" form={form} errors={errors} onChange={handleChange} />
        <div>
          <label className="label">Product Price (₹) <span className="text-red-500">*</span></label>
          <input
            className={`input-field ${errors.productPrice ? "border-red-500" : ""}`}
            name="productPrice" type="number" min="0" step="0.01"
            value={form.productPrice} onChange={handleChange} placeholder="0.00"
          />
          {errors.productPrice && <p className="text-red-500 text-xs mt-1">{errors.productPrice}</p>}
        </div>
        <div>
          <label className="label">Purchase Date <span className="text-red-500">*</span></label>
          <input
            className={`input-field ${errors.purchaseDate ? "border-red-500" : ""}`}
            name="purchaseDate" type="date"
            max={new Date().toISOString().slice(0, 10)}
            value={form.purchaseDate} onChange={handleChange}
          />
          {errors.purchaseDate && <p className="text-red-500 text-xs mt-1">{errors.purchaseDate}</p>}
        </div>
        <div>
          <label className="label">Rudraksha Size <span style={{ color: "#5a4a30", fontWeight: 400 }}>(Optional)</span></label>
          <input
            className="input-field"
            name="rudrakshaSize" type="text"
            value={form.rudrakshaSize} onChange={handleChange}
            placeholder="e.g. 4 Mukhi, 10mm, 5 Face"
          />
        </div>
        <div className="md:col-span-2">
          <label className="label">Description <span style={{ color: "#5a4a30", fontWeight: 400 }}>(Optional)</span></label>
          <textarea
            className="input-field resize-none"
            name="productDescription" rows={2}
            value={form.productDescription} onChange={handleChange}
            placeholder="Optional details about the product"
          />
        </div>
      </div>

      {/* Image Upload */}
      <div>
        <label className="label">
          Product Photos <span style={{ color: "#5a4a30", fontWeight: 400 }}>(Optional — select multiple)</span>
        </label>
        <label className="flex flex-col items-center justify-center w-full cursor-pointer rounded-lg py-5 transition-all"
          style={{ border: "2px dashed rgba(200,150,12,0.4)", background: "rgba(200,150,12,0.03)" }}
          onMouseEnter={e => e.currentTarget.style.borderColor = "#C8960C"}
          onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(200,150,12,0.4)"}
        >
          <svg className="w-8 h-8 mb-2" style={{ color: "#8A6000" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-sm" style={{ color: "#C8960C" }}>Click to select photos</span>
          <span className="text-xs mt-1" style={{ color: "#5a4a30" }}>JPG, PNG, WEBP — multiple allowed</span>
          <input type="file" multiple accept="image/*" className="hidden" onChange={handleImages} />
        </label>

        {/* New image previews */}
        {previews.length > 0 && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {previews.map((src, i) => (
              <div key={i} className="relative group">
                <img src={src} alt="" className="w-16 h-16 object-cover rounded-lg" style={{ border: "1px solid rgba(200,150,12,0.4)" }} />
                <button type="button" onClick={() => removeImage(i)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-xs flex items-center justify-center"
                  style={{ background: "#7f1d1d", color: "#fecaca" }}>✕</button>
                {i === 0 && <span className="absolute bottom-0 left-0 right-0 text-center text-white rounded-b-lg" style={{ fontSize: 9, background: "rgba(200,150,12,0.8)" }}>Card</span>}
              </div>
            ))}
          </div>
        )}

        {/* Existing images */}
        {existing?.images?.length > 0 && (
          <div className="mt-3">
            <p className="text-xs mb-2" style={{ color: "#8A6000" }}>Existing photos:</p>
            <div className="flex gap-2 flex-wrap">
              {existing.images.map((img, i) => (
                <div key={img.filename} className="relative">
                  <img src={img.url} alt="" className="w-16 h-16 object-cover rounded-lg" style={{ border: "1px solid rgba(200,150,12,0.4)" }} />
                  {i === 0 && <span className="absolute bottom-0 left-0 right-0 text-center text-white rounded-b-lg" style={{ fontSize: 9, background: "rgba(200,150,12,0.8)" }}>Card</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Video Upload */}
      <div>
        <label className="label">Product Videos <span style={{ color: "#5a4a30", fontWeight: 400 }}>(Optional)</span></label>
        <input
          type="file" multiple accept="video/*"
          onChange={(e) => setVideos([...e.target.files])}
          className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-medium"
          style={{ color: "#8A6000" }}
        />
        {videos.length > 0 && <p className="text-xs mt-1" style={{ color: "#8A6000" }}>{videos.length} video(s) selected</p>}
      </div>

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={loading} className="btn-primary flex-1">
          {loading ? "Saving…" : existing ? "Update Product" : "Save & Generate Card"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        )}
      </div>
    </form>
  );
}
